import { PHONE_TABS, type PhoneTab } from '@player/domain';
import { FONT_STACK, colors } from '../theme.js';
import { useTranslator } from '../i18n/I18nProvider.js';

/**
 * La barra in basso del telefono: cinque schede.
 *
 * ## Dai token del tema, non dai colori del prototipo
 *
 * Il prototipo scrive `#FFB547` e `font:600 14px Onest` dentro ogni elemento. Copiarli a mano è già
 * costato caro su questo progetto: cinquantotto dichiarazioni `font:` in linea avevano annullato il
 * carattere dell'applicazione, perché la forma abbreviata reimposta la famiglia. Qui si usano
 * `colors` e `FONT_STACK`, che sono **la stessa tavolozza** letta da un posto solo.
 *
 * ## Perché non ha stato
 *
 * Quale scheda sia accesa lo decide `tabFor` nel dominio, e arriva come proprietà. Calcolarlo qui
 * avrebbe voluto dire una regola non verificabile se non montando un componente e guardando un
 * colore — mentre è esattamente una regola: l'aggregatore accende Home, il lettore accende Guida o
 * On demand secondo cosa riproduce.
 *
 * ## `active` può essere `null`, e si vede
 *
 * Una barra con tutte le schede spente significa «siamo in un posto che nessuno ha mappato», e deve
 * **restare visibile**. È il segnale di una voce aggiunta alla TV e dimenticata qui: spenta si nota
 * subito, mentre una scheda accesa a caso non la nota nessuno.
 *
 * ## Tocco, non fuoco
 *
 * Nessuna gestione del fuoco direzionale: sul telefono si tocca. Le aree sono alte 56 px — sotto i
 * 44 px raccomandati il dito sbaglia, e cinque bersagli su 390 px di larghezza ne lasciano 78 a
 * testa, abbastanza perché il pollice non prenda quella accanto.
 */
export function TabBar({
  active,
  onPick,
}: {
  /** La scheda accesa, o `null` dove nessuna lo è. */
  readonly active: PhoneTab | null;
  readonly onPick: (tab: PhoneTab) => void;
}) {
  const translator = useTranslator();

  return (
    <nav
      style={{
        flex: 'none',
        display: 'flex',
        /*
          `env(safe-area-inset-bottom)`: sui telefoni senza tasto fisico, in fondo c'è la barra di
          sistema. Senza questo margine le cinque schede ci finiscono sotto, e si tocca il sistema
          invece dell'app — un difetto che non si vede su nessun emulatore a schermo intero.
        */
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        background: colors.surface,
        borderTop: `1px solid ${colors.borderFaint}`,
      }}
    >
      {PHONE_TABS.map((tab) => {
        const acceso = tab === active;
        return (
          <button
            key={tab}
            type="button"
            onClick={() => onPick(tab)}
            /*
              `aria-current` e non solo il colore: su un telefono il lettore di schermo è il modo in
              cui qualcuno che non vede sa dov'è, e l'ambra da sola non glielo dice.
            */
            aria-current={acceso ? 'page' : undefined}
            style={{
              flex: 1,
              minWidth: 0,
              height: 56,
              border: 0,
              background: 'none',
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              color: acceso ? colors.accent : colors.textDim,
              font: `${acceso ? 600 : 500} 11px ${FONT_STACK}`,
              padding: 0,
            }}
          >
            {/*
              La barretta sopra la scheda accesa, dal prototipo.

              Resta nel documento anche da spenta, trasparente: togliendola, le etichette delle altre
              quattro salterebbero di tre pixel ogni volta che si cambia scheda.
            */}
            <span
              aria-hidden
              style={{
                width: 22,
                height: 3,
                borderRadius: 2,
                background: acceso ? colors.accent : 'transparent',
              }}
            />
            <span
              style={{
                maxWidth: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {translator.t(`phone.tab.${tab}`)}
            </span>
          </button>
        );
      })}
    </nav>
  );
}
